import {
  action,
  computed,
  IReactionDisposer,
  makeObservable,
  observable,
  reaction,
  runInAction,
} from 'mobx'
import { task } from 'mobx-task'
import { Subscription } from 'rxjs'
import { Id, Router } from 'types/definitions'
import { PrefilledDraftInput } from 'jobs/creation/PrefilledDraftInput'
import JobCreationRootViewModel from 'jobs/creation/JobCreationRootViewModel'
import JobCreationViewModel from 'jobs/creation/JobCreationViewModel'
import Workspace from 'iam/models/Workspace'
import Member from 'iam/models/Member'
import Job from 'jobs/Job'
import { LegendServiceType } from 'legends/PublishedLegend'
import link from 'infra/link'
import CreateJobConfirmationDialogViewModel from '../CreateJobConfirmationDialog/CreateJobConfirmationDialogViewModel'
import { AlertFactory, AlertState } from '../../../alerts/Alert'
import { Membership } from '../../../iam/WorkspaceMembershipStore'
import JobCheckoutViewModel from '../../../jobs/views/JobRoot/Checkout/JobCheckoutViewModel'
import SummaryStore from '../../../jobs/SummaryStore'
import CouponStore from '../../../billing/CouponStore'
import PublishedLegendStore from '../../../legends/PublishedLegendStore'
import JobStore from '../../../jobs/JobStore'
import { toStream } from '../../../utils/mobx-util'
import { debounceTime, filter } from 'rxjs/operators'
import AddCouponDialogViewModel from '../AddCouponDialog/AddCouponDialogViewModel'
import BillingStore from '../../../billing/BillingStore'

export default class CallCenterJobViewModel {
  private subscriptions: Subscription[] = []

  private autoLegendDisposer: IReactionDisposer | null = null

  @observable
  activeWorkspace: Workspace | null = null

  @observable
  activeMember: Member | null = null

  @observable
  requiredQuestionsAmount = 0

  confirmationDialog: CreateJobConfirmationDialogViewModel

  jobCheckoutViewModel: JobCheckoutViewModel

  addCouponViewModel: AddCouponDialogViewModel

  constructor(
    private readonly jobCreationRoot: JobCreationRootViewModel,
    summaryStore: SummaryStore,
    couponStore: CouponStore,
    publishedLegendStore: PublishedLegendStore,
    private jobStore: JobStore,
    private billingStore: BillingStore,
    private readonly newAlert: AlertFactory,
    private readonly router: Router,
  ) {
    makeObservable(this)

    this.confirmationDialog = new CreateJobConfirmationDialogViewModel(
      (membership) => this.initializeJob(membership),
    )

    this.jobCheckoutViewModel = new JobCheckoutViewModel(
      Id.none,
      summaryStore,
      jobStore,
      publishedLegendStore,
      couponStore,
      newAlert,
    )

    this.addCouponViewModel = new AddCouponDialogViewModel(
      () => this.jobCheckoutViewModel.job,
      jobStore,
      newAlert,
      () =>
        this.billingStore.invalidateCheckoutSessions(
          this.jobCheckoutViewModel.job.id,
          true,
        ),
    )
  }

  @computed
  get hasActiveClient() {
    return Boolean(this.activeMember && this.activeWorkspace)
  }

  @computed
  get activeMemberName() {
    return this.activeMember?.displayName ?? ''
  }

  @computed
  get jobCreation(): JobCreationViewModel | null {
    if (!this.hasActiveClient) {
      return null
    }

    return this.jobCreationRoot.current
  }

  @computed
  get requiredQuestions() {
    return this.jobCreation?.intakeQuestions.questionList.questions
      .slice(0, this.requiredQuestionsAmount)
      .map((question) => question)
  }

  @computed
  get isReady() {
    return this.requiredQuestions?.every((question) => question.isAnswered)
  }

  @computed
  get isCheckoutReady() {
    if (this.requiredQuestionsAmount === 0) {
      return true
    }

    return (
      this.requiredQuestions?.every((question) => question.isAnswered) ?? false
    )
  }

  @computed
  get isEmailingCheckoutLink() {
    return this.emailCheckoutLink.pending
  }

  @computed
  get jobScopeItems() {
    const job = this.jobCheckoutViewModel.job

    return this.jobCheckoutViewModel.job.jobScopeItems.filter(
      (item) => item.id !== job.jobBundle?.id,
    )
  }

  @action.bound
  resetActiveJobState() {
    this.activeWorkspace = null
    this.activeMember = null
    this.requiredQuestionsAmount = 0

    this.autoLegendDisposer?.()
    this.autoLegendDisposer = null
  }

  @action.bound
  goToCustomerLookup() {
    this.resetActiveJobState()
    this.router.push('/call-center')
  }

  @action.bound
  goBackToJob() {
    this.router.push(
      link.callCenterJob(Id.assert(this.jobCheckoutViewModel.id)),
    )
  }

  async loadFromExistingJob(job: Job) {
    try {
      await this.loadExistingJob(job)
    } catch (error) {
      console.error('Failed to load existing job in call center', error)
    }
  }

  initializeJob = task.resolved(async (membership: Membership) => {
    const workspaceId = Id.assert(membership.workspace.id)
    const ownerPublicId = Id.assert(membership.member.user.publicId)

    const prefilled = new PrefilledDraftInput(
      undefined,
      ownerPublicId,
      null,
      [],
      {
        publicId: ownerPublicId,
        workspaceIds: [workspaceId],
      },
    )

    const editor = await this.jobCreationRoot.edit(Id.none, prefilled)

    runInAction(() => {
      editor.workspaceSelector.selectWorkspace(membership.workspace)
      editor.ownerSelector.selectOwner(membership.member)

      this.activeWorkspace = membership.workspace
      this.activeMember = membership.member
      this.requiredQuestionsAmount = this.getRequiredQuestionAmount(
        membership.workspace,
      )
    })

    this.setupLegendAutoSelection(editor)
    this.confirmationDialog.close()
  })

  loadExistingJob = task.resolved(async (job: Job) => {
    this.jobCheckoutViewModel.id = job.id
    await this.jobCheckoutViewModel.fetchJobInfo()

    // Load the job into the job creation editor.
    const editor = await this.jobCreationRoot.edit(
      Id.assert(job.id),
      PrefilledDraftInput.empty(),
    )

    runInAction(() => {
      this.activeWorkspace = job.workspace
      this.activeMember = job.owner || null
      this.requiredQuestionsAmount = this.getRequiredQuestionAmount(
        job.workspace,
      )
    })

    this.setupLegendAutoSelection(editor)
  })

  activate() {
    this.jobCreationRoot.activate()

    this.subscriptions.push(
      this.jobCreationRoot.jobCreated$.subscribe((job) => {
        if (job === Job.none) {
          return
        }

        this.jobCheckoutViewModel.id = job.id
        const memberName = this.activeMember?.displayName ?? ''
        const workspaceName = this.activeWorkspace?.name || 'workspace'

        this.newAlert()
          .withState(AlertState.Success)
          .saying(
            'Job created',
            `Draft job created for ${memberName} in ${workspaceName}.`,
          )
          .autoDismiss()
          .show()

        // Navigate to the new job URL
        this.router.push(link.callCenterJob(Id.assert(job.id)))
      }),
    )

    this.subscriptions.push(
      toStream(() => this.requiredQuestions)
        .pipe(
          filter(() => this.jobCheckoutViewModel.job.id !== Id.none),
          debounceTime(300),
        )
        .subscribe(() => {
          void this.jobCheckoutViewModel.fetchJobInfo()

          void this.billingStore.invalidateCheckoutSessions(
            this.jobCheckoutViewModel.job.id,
            false,
          )
        }),
    )
  }

  deactivate() {
    this.resetActiveJobState()
    this.jobCreationRoot.deactivate()
    this.jobCheckoutViewModel.deactivate()

    this.subscriptions.forEach((subscription) => subscription.unsubscribe())
    this.subscriptions = []

    this.autoLegendDisposer?.()
    this.autoLegendDisposer = null
  }

  private getRequiredQuestionAmount(workspace: Workspace) {
    const config = workspace.config?.call_center_config
    const value = config?.total_required_questions
    return value ?? 0
  }

  private setupLegendAutoSelection(editor: JobCreationViewModel) {
    this.autoLegendDisposer?.()

    this.autoLegendDisposer = reaction(
      () => editor.legendSelector.legends.map((legend) => legend.id),
      () => {
        const selected = editor.legendSelector.selectedLegendVersion
        const taxTypeLegends = editor.legendSelector.legends.filter(
          (l) => l.serviceType === LegendServiceType.Tax,
        )
        if (selected && selected.serviceType !== LegendServiceType.Tax) {
          editor.legendSelector.selectLegend(null)
        }

        if (editor.legendSelector.selectedLegendVersion) {
          return
        }

        if (taxTypeLegends.length === 1) {
          editor.legendSelector.selectLegend(taxTypeLegends[0], true)
        }
      },
      { fireImmediately: true },
    )
  }

  removeCoupon = task.resolved(async () => {
    const alert = this.newAlert()

    try {
      await this.billingStore.invalidateCheckoutSessions(
        this.jobCheckoutViewModel.job.id,
        true,
      )

      await this.jobStore.callCenterRemoveCouponByCode(
        this.jobCheckoutViewModel.job.id,
      )

      alert
        .withState(AlertState.Success)
        .saying('Coupon removed', `Coupon removed.`)
        .show()
    } catch (error) {
      alert.fromError(error).show()

      throw error
    }
  })

  emailCheckoutLink = task.resolved(async () => {
    const job = this.jobCheckoutViewModel.job
    const userPublicId = job.championClient?.member.user.publicId

    if (!userPublicId) {
      return
    }

    const alert = this.newAlert()

    try {
      // It's not necessary to generate a link before sending an email.
      // Generating a link now creates a Stripe checkout session now, which is
      // used for analytics. Generating the checkout link now is solely for analytics.
      // The user will only receive an email containing the stable link.
      await this.billingStore.generateCheckoutLink(job.id)
      await this.billingStore.emailCheckoutLink({
        workspace_id: Id.assert(job.workspace.id),
        user_id: Id.assert(userPublicId),
        job_id: Id.assert(job.id),
      })

      alert
        .withState(AlertState.Success)
        .saying('Checkout link email sent')
        .autoDismiss()
        .show()
    } catch (error) {
      alert.fromError(error).autoDismiss().show()

      throw error
    }
  })
}
